import styled from "@emotion/styled";
import { AnimatePresence, motion } from "motion/react";
import { FC, useCallback, useMemo } from "react";
import { HeadsUpFindMoreButton } from "./heads-up-find-more-button";
import { HeadsUpViewState } from "@/components/heads-up/context/heads-up-context";
import { HeadsUpFindMoreMessage } from "./heads-up-find-more-message";
import { renderSmartCard, type RenderSmartCardOptions } from "@/components/heads-up/cards";
import type { SmartCardRow } from "@/domains/tanstack-db";
import { useHeadsUpAnalytics } from "@/components/heads-up/analytics";

interface HeadsUpFindMoreSectionProps {
  viewState: HeadsUpViewState;
  onFindMore: () => void;
  isFindingMore: boolean;
  isDisabled: boolean;
  hasFoundMore: boolean;
  findMoreCards: SmartCardRow[];
  cardRenderOptions: RenderSmartCardOptions;
}

export const HeadsUpFindMoreSection: FC<HeadsUpFindMoreSectionProps> = ({
  viewState,
  onFindMore,
  isFindingMore,
  isDisabled,
  hasFoundMore,
  findMoreCards,
  cardRenderOptions,
}) => {
  const analytics = useHeadsUpAnalytics();
  const hasFindMoreCards = findMoreCards.length > 0;
  const shouldShowButton = useMemo(() => {
    if (hasFoundMore) return false;
    if (hasFindMoreCards) return false;
    return true;
  }, [hasFoundMore, hasFindMoreCards]);
  const animateDelay = isFindingMore ? 5000 : 0;
  const count = findMoreCards.length;

  const handleFindMoreClick = useCallback(() => {
    analytics.trackFindMoreClick();
    onFindMore();
  }, [analytics, onFindMore]);

  const buildSectionAnalyticsHandlers = useCallback(
    (sectionPositionIndex: number) => ({
      sectionPositionIndex,
      onItemPreviewOpen: analytics.trackHover,
      onItemClick: analytics.trackClick,
    }),
    [analytics]
  );

  /**
   * We only show the footer when we have generated some content.
   */

  if (viewState !== HeadsUpViewState.GeneratedHasContent) {
    return null;
  }

  return (
    <FindMoreContainer>
      {shouldShowButton ? (
        <ButtonWrapper>
          <HeadsUpFindMoreButton
            onClick={handleFindMoreClick}
            isLoading={isFindingMore}
            isDisabled={isDisabled}
          />
        </ButtonWrapper>
      ) : null}

      <AnimatePresence>
        {hasFoundMore ? (
          <AnimatedMessageContainer
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, delay: animateDelay }}
          >
            <DividerRow>
              <DividerSvg viewBox="0 0 1000 1" preserveAspectRatio="none" aria-hidden>
                <line
                  x1="0"
                  y1="0"
                  x2="1000"
                  y2="0"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeDasharray="2.5 2.5"
                  vectorEffect="non-scaling-stroke"
                  shapeRendering="crispEdges"
                />
              </DividerSvg>
              <MessageLabel>
                <HeadsUpFindMoreMessage isLoading={isFindingMore} count={count} />
              </MessageLabel>
            </DividerRow>
          </AnimatedMessageContainer>
        ) : null}
      </AnimatePresence>

      {hasFindMoreCards ? (
        <FindMoreCardList>
          {findMoreCards.map((card, index) => {
            const content = renderSmartCard(card, {
              ...cardRenderOptions,
              analytics: buildSectionAnalyticsHandlers(index),
            });
            if (!content) return null;

            return <FindMoreCardItem key={card.id}>{content}</FindMoreCardItem>;
          })}
        </FindMoreCardList>
      ) : null}
    </FindMoreContainer>
  );
};

const FindMoreContainer = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["3"]};
  padding-top: ${({ theme }) => theme.spacing["4"]};
  padding-bottom: ${({ theme }) => theme.spacing["5"]};
  background: ${({ theme }) => theme.colors.surface.headsUp.default};
  flex-shrink: 0;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const AnimatedMessageContainer = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const DividerRow = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  isolation: isolate; /* create stacking context for z-index layering */
  color: ${({ theme }) => theme.colors.border.onAccent};
  padding: 0 ${({ theme }) => theme.spacing["5"]};
  height: 46px;
  overflow: hidden;
  margin-top: -20px;
  margin-bottom: -4px;
`;

const DividerSvg = styled.svg`
  position: absolute;
  top: 50%;
  left: var(--divider-inset);
  right: var(--divider-inset);
  height: 1px;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 0;
`;

const MessageLabel = styled.span`
  margin-top: 28px;
  position: relative;
  z-index: 1;
  padding: 0 6px;
  background: ${({ theme }) => theme.colors.surface.headsUp.default};
`;

const FindMoreCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["3"]};
`;

const FindMoreCardItem = styled.div`
  display: flex;
  flex-direction: column;
`;
